import React, { useState, useEffect } from 'react';
import { 
  X, 
  Download, 
  Smartphone, 
  Monitor, 
  Globe, 
  ShieldCheck, 
  CheckCircle2, 
  ExternalLink, 
  Layers, 
  Sparkles, 
  ArrowRight, 
  QrCode, 
  Zap, 
  Lock, 
  UploadCloud,
  Check,
  FolderDown,
  Puzzle,
  AlertCircle,
  HelpCircle,
  Github,
  MousePointer2
} from 'lucide-react';
import { CkLogo } from './CkLogo';
import { CURRENT_APP_VERSION } from '../utils/updateChecker';

interface DownloadHubModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenGuide: (tab?: 'android' | 'windows' | 'security') => void;
}

export const DownloadHubModal: React.FC<DownloadHubModalProps> = ({ isOpen, onClose, onOpenGuide }) => {
  const [activeTab, setActiveTab] = useState<'all' | 'android' | 'windows' | 'flow' | 'web'>('all');

  const [showAndroidQr, setShowAndroidQr] = useState(false);
  const [showSafetyHelp, setShowSafetyHelp] = useState(false);
  const [detectedOs, setDetectedOs] = useState<'windows' | 'android' | 'mac' | 'linux' | 'other'>('windows');

  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      const ua = navigator.userAgent;
      if (/Android/i.test(ua)) setDetectedOs('android');
      else if (/Windows/i.test(ua)) setDetectedOs('windows');
      else if (/Macintosh|Mac OS X/i.test(ua)) setDetectedOs('mac');
      else if (/Linux/i.test(ua)) setDetectedOs('linux');
      else setDetectedOs('other');
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/85 backdrop-blur-2xl animate-fadeIn">
      
      {/* Modal Container */}
      <div className="relative w-full max-w-5xl max-h-[92vh] flex flex-col bg-[#070912]/95 border border-cyan-500/30 rounded-3xl shadow-[0_25px_80px_rgba(0,0,0,0.9),0_0_50px_rgba(0,240,255,0.15)] overflow-hidden">
        
        {/* Background Ambient Glows */}
        <div className="absolute -top-32 -left-32 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-purple-600/15 rounded-full blur-3xl pointer-events-none" />

        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 sm:px-8 py-4 sm:py-5 border-b border-white/10 bg-white/[0.02] relative z-10">
          <div className="flex items-center gap-3.5">
            <CkLogo size={40} className="drop-shadow-[0_0_15px_rgba(0,240,255,0.4)]" />
            <div>
              <div className="flex items-center gap-2.5">
                <h2 className="text-xl sm:text-2xl font-extrabold text-white font-display tracking-tight">
                  CK Share <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">Download Hub</span>
                </h2>
                <span className="px-2.5 py-0.5 text-[10px] font-bold font-mono text-cyan-300 bg-cyan-500/15 border border-cyan-500/30 rounded-full">
                  v{CURRENT_APP_VERSION} Stable
                </span>
              </div>
              <p className="text-xs text-slate-400 hidden sm:block">Official native packages, companion binaries, and zero-install clients</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-2xl hover:bg-white/10 transition-colors cursor-pointer"
            title="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Dynamic OS Recommendation Strip */}
        <div className="px-6 sm:px-8 py-2.5 bg-gradient-to-r from-cyan-950/40 via-purple-950/20 to-black/40 border-b border-white/5 flex items-center justify-between flex-wrap gap-2 text-xs">
          <div className="flex items-center gap-2 text-slate-300">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span>Detected OS: <strong className="text-white capitalize">{detectedOs}</strong></span>
          </div>

          <div className="flex items-center gap-3">
            {detectedOs === 'windows' && (
              <a
                href="/downloads/CKShareTray.exe"
                download="CKShareTray.exe"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-bold text-cyan-300 hover:text-cyan-200 transition-colors"
              >
                <span>⚡ Download Windows Companion (.exe)</span>
                <ArrowRight size={13} />
              </a>
            )}
            {detectedOs === 'android' && (
              <a
                href="/downloads/ck-share-android.apk"
                download="ck-share-android.apk"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-bold text-emerald-300 hover:text-emerald-200 transition-colors"
              >
                <span>⚡ Download Android APK</span>
                <ArrowRight size={13} />
              </a>
            )}
            <button
              onClick={() => setShowSafetyHelp(!showSafetyHelp)}
              className="text-cyan-400 hover:text-cyan-300 font-semibold inline-flex items-center gap-1 transition-colors cursor-pointer"
            >
              <HelpCircle size={13} />
              <span>Browser Safety Guide</span>
            </button>
          </div>
        </div>

        {/* Browser Safety & SmartScreen Help Banner */}
        {showSafetyHelp && (
          <div className="px-6 sm:px-8 py-3.5 bg-[#0a1020] border-b border-cyan-500/30 text-xs text-slate-300 space-y-2 animate-fadeIn">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-bold text-cyan-300">
                <ShieldCheck size={16} />
                <span>Why does Chrome or Windows SmartScreen show a prompt?</span>
              </div>
              <button onClick={() => setShowSafetyHelp(false)} className="text-slate-400 hover:text-white">
                <X size={14} />
              </button>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              CK Share is 100% free and open source. Because new release binaries haven't built millions of downloads in Microsoft/Google reputation databases yet, browsers show standard informational prompts:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div className="p-2.5 rounded-xl bg-black/40 border border-white/5 space-y-1">
                <strong className="text-white flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-cyan-400" /> Google Chrome:
                </strong>
                <p className="text-[11px] text-slate-400">
                  If Chrome shows <em>"File isn't commonly downloaded"</em>, click the 3 dots ➔ <strong>Keep</strong> ➔ <strong>Keep anyway</strong>. All downloads are served over encrypted HTTPS with HSTS.
                </p>
              </div>
              <div className="p-2.5 rounded-xl bg-black/40 border border-white/5 space-y-1">
                <strong className="text-white flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-purple-400" /> Windows SmartScreen:
                </strong>
                <p className="text-[11px] text-slate-400">
                  Click <strong>More info</strong> ➔ <strong>Run anyway</strong>. You can verify the full open-source C# codebase directly on GitHub anytime!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Segmented Platform Filter Tabs */}
        <div className="flex items-center gap-2 px-6 sm:px-8 py-3 border-b border-white/5 bg-black/40 overflow-x-auto no-scrollbar">
          {[
            { id: 'all', label: 'All Platforms', icon: Layers },
            { id: 'android', label: 'Android Phone', icon: Smartphone },
            { id: 'windows', label: 'CK Share (Windows)', icon: Monitor },
            { id: 'flow', label: 'CK Flow (Multi-PC)', icon: MousePointer2 },
            { id: 'web', label: 'Web & PWA', icon: Globe },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-1.5 text-xs font-semibold rounded-xl transition-all whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-400/40 shadow-[0_0_15px_rgba(0,240,255,0.2)]'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-white/5 border border-transparent'
                }`}
              >
                <Icon size={14} className={isActive ? 'text-cyan-400' : 'text-slate-500'} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-6">
          
          {/* Grid of Platform Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
            
            {/* 1. ANDROID APK CARD */}
            {(activeTab === 'all' || activeTab === 'android') && (
              <div className="p-6 sm:p-7 rounded-3xl bg-gradient-to-b from-white/[0.04] via-black/60 to-black/90 border border-white/10 hover:border-emerald-500/50 shadow-2xl transition-all flex flex-col justify-between group relative overflow-hidden">
                
                {/* Ambient Corner */}
                <div className="absolute top-0 right-0 w-36 h-36 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />

                <div className="space-y-4">
                  
                  {/* Card Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3.5">
                      <div className="w-13 h-13 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.2)] group-hover:scale-105 transition-transform">
                        <Smartphone size={26} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-white font-display">Android Application</h3>
                          <span className="px-2 py-0.5 text-[9px] font-bold font-mono text-emerald-300 bg-emerald-500/20 border border-emerald-500/40 rounded-full">
                            Official APK
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">Android 8.0 (Oreo) through Android 15+</p>
                      </div>
                    </div>
                  </div>

                  {/* Feature Badges */}
                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                    <span className="px-2.5 py-0.5 rounded-lg bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 font-semibold flex items-center gap-1">
                      <Zap size={11} />
                      Accessibility Service
                    </span>
                    <span className="px-2.5 py-0.5 rounded-lg bg-cyan-950/40 border border-cyan-500/30 text-cyan-300 font-semibold flex items-center gap-1">
                      <Lock size={11} />
                      AES-GCM-256
                    </span>
                    <span className="px-2.5 py-0.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 font-semibold">
                      Background Daemon
                    </span>
                  </div>

                  <p className="text-xs text-slate-300 leading-relaxed">
                    Native Android client with continuous background clipboard broadcast and foreground copy detection.
                  </p>

                  {/* Highlights Bullet List */}
                  <div className="space-y-2 text-xs text-slate-400 bg-black/40 p-3.5 rounded-2xl border border-white/5">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-emerald-400 flex-shrink-0" />
                      <span>Copies from Chrome, WhatsApp, Notes, Gmail, Telegram</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-emerald-400 flex-shrink-0" />
                      <span>Notification tray button: <strong>"Sync Clipboard Now"</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-emerald-400 flex-shrink-0" />
                      <span>Survives app close &amp; swipe with battery-efficient service</span>
                    </div>
                  </div>

                  {/* Interactive Phone QR Scanner Toggle */}
                  {showAndroidQr && (
                    <div className="p-4 rounded-2xl bg-black/80 border border-emerald-500/40 flex flex-col sm:flex-row items-center gap-4 animate-fadeIn">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent('https://ck-share.pages.dev/downloads/ck-share-android.apk')}&bgcolor=000000&color=00f0ff&margin=6`}
                        alt="Android APK Direct Download QR Code"
                        className="w-28 h-28 rounded-xl border border-cyan-500/40 p-1 bg-black shadow-lg"
                      />
                      <div className="space-y-1 text-center sm:text-left">
                        <h4 className="text-xs font-bold text-white">Scan with Phone Camera</h4>
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                          Point your phone camera at this QR code to download and install <code className="text-cyan-300 font-mono">ck-share-android.apk</code> directly.
                        </p>
                      </div>
                    </div>
                  )}

                </div>

                {/* Android Action Bar */}
                <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-2.5">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowAndroidQr(!showAndroidQr)}
                      className="p-2 rounded-xl text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors cursor-pointer"
                      title="Show QR Code for Phone Camera"
                    >
                      <QrCode size={15} className="text-cyan-400" />
                    </button>
                    <button
                      onClick={() => {
                        onClose();
                        onOpenGuide('android');
                      }}
                      className="px-3 py-1.5 rounded-xl text-xs text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors cursor-pointer"
                    >
                      Setup Guide
                    </button>
                  </div>

                  <a
                    href="/downloads/ck-share-android.apk"
                    download="ck-share-android.apk"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-2 text-xs font-bold text-black bg-gradient-to-r from-cyan-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.3)] hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] transition-all hover:scale-105 cursor-pointer"
                  >
                    <Download size={14} />
                    <span>Download Android APK</span>
                  </a>
                </div>

              </div>
            )}

            {/* 2. CK SHARE WINDOWS CARD */}
            {(activeTab === 'all' || activeTab === 'windows') && (
              <div className="p-6 sm:p-7 rounded-3xl bg-gradient-to-b from-white/[0.04] via-black/60 to-black/90 border border-white/10 hover:border-purple-500/50 shadow-2xl transition-all flex flex-col justify-between group relative overflow-hidden">
                
                {/* Ambient Corner */}
                <div className="absolute top-0 right-0 w-36 h-36 bg-purple-500/10 rounded-full blur-2xl pointer-events-none" />

                <div className="space-y-4">
                  
                  {/* Card Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3.5">
                      <div className="w-13 h-13 rounded-2xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400 shadow-[0_0_20px_rgba(168,85,247,0.2)] group-hover:scale-105 transition-transform">
                        <Monitor size={26} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-white font-display">CK Share Windows</h3>
                          <span className="px-2 py-0.5 text-[9px] font-bold font-mono text-purple-300 bg-purple-500/20 border border-purple-500/40 rounded-full">
                            Clipboard &amp; Files
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">Windows 10 &amp; Windows 11 (64-bit)</p>
                      </div>
                    </div>
                  </div>

                  {/* Feature Badges */}
                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                    <span className="px-2.5 py-0.5 rounded-lg bg-purple-950/40 border border-purple-500/30 text-purple-300 font-semibold flex items-center gap-1">
                      <Zap size={11} />
                      Win32 Clipboard Hook
                    </span>
                    <span className="px-2.5 py-0.5 rounded-lg bg-cyan-950/40 border border-cyan-500/30 text-cyan-300 font-semibold flex items-center gap-1">
                      <UploadCloud size={11} />
                      QuickDrop Hub
                    </span>
                    <span className="px-2.5 py-0.5 rounded-lg bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 font-semibold">
                      AES-GCM-256 E2EE
                    </span>
                  </div>

                  <p className="text-xs text-slate-300 leading-relaxed">
                    Lightweight standalone Win32 desktop companion. Real-time clipboard hooks, drag-and-drop file sharing, and instant sync with your phone.
                  </p>

                  {/* Highlights Bullet List */}
                  <div className="space-y-2 text-xs text-slate-400 bg-black/40 p-3.5 rounded-2xl border border-white/5">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-purple-400 flex-shrink-0" />
                      <span>Zero-Click Ctrl+C / Ctrl+V sync across all Windows applications</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-cyan-400 flex-shrink-0" />
                      <span>📁 <strong>Native QuickDrop Hub:</strong> Drag &amp; drop files directly to phone</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-emerald-400 flex-shrink-0" />
                      <span>Runs silently in taskbar tray with low memory footprint</span>
                    </div>
                  </div>

                </div>

                {/* Windows Action Bar */}
                <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-2.5">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        onClose();
                        onOpenGuide('windows');
                      }}
                      className="px-3 py-1.5 rounded-xl text-xs text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors cursor-pointer"
                    >
                      Setup Guide
                    </button>
                    <a
                      href="/downloads/ck-share-windows.zip"
                      download="ck-share-windows.zip"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-xl text-xs text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors flex items-center gap-1 cursor-pointer"
                      title="Download Portable ZIP"
                    >
                      <FolderDown size={13} />
                      <span>Portable ZIP</span>
                    </a>
                  </div>

                  <a
                    href="/downloads/CKShareTray.exe"
                    download="CKShareTray.exe"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-2 text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-xl shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_25px_rgba(168,85,247,0.5)] transition-all hover:scale-105 cursor-pointer"
                  >
                    <Download size={14} />
                    <span>Download CK Share (.exe)</span>
                  </a>
                </div>

              </div>
            )}

            {/* 3. CK FLOW MULTI-PC CARD */}
            {(activeTab === 'all' || activeTab === 'flow') && (
              <div className="p-6 sm:p-7 rounded-3xl bg-gradient-to-b from-cyan-950/30 via-black/70 to-black/90 border border-cyan-500/40 hover:border-cyan-400 shadow-2xl transition-all flex flex-col justify-between group relative overflow-hidden">
                
                {/* Ambient Corner */}
                <div className="absolute top-0 right-0 w-36 h-36 bg-cyan-500/15 rounded-full blur-2xl pointer-events-none" />

                <div className="space-y-4">
                  
                  {/* Card Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3.5">
                      <div className="w-13 h-13 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 shadow-[0_0_20px_rgba(0,240,255,0.3)] group-hover:scale-105 transition-transform">
                        <MousePointer2 size={26} className="transform -rotate-45" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-white font-display">CK Flow Multi-PC</h3>
                          <span className="px-2 py-0.5 text-[9px] font-bold font-mono text-cyan-300 bg-cyan-500/20 border border-cyan-500/40 rounded-full">
                            Mouse &amp; Keyboard Mesh
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">1 Mouse &amp; Keyboard for Multiple Computers</p>
                      </div>
                    </div>
                  </div>

                  {/* Feature Badges */}
                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                    <span className="px-2.5 py-0.5 rounded-lg bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 font-semibold flex items-center gap-1">
                      <Zap size={11} />
                      Sub-1ms UDP Mesh
                    </span>
                    <span className="px-2.5 py-0.5 rounded-lg bg-cyan-950/40 border border-cyan-500/30 text-cyan-300 font-semibold flex items-center gap-1">
                      <Sparkles size={11} />
                      Infinite Delta Trapping
                    </span>
                    <span className="px-2.5 py-0.5 rounded-lg bg-purple-950/40 border border-purple-500/30 text-purple-300 font-semibold">
                      Gaming Lock (Ctrl+Ctrl)
                    </span>
                  </div>

                  <p className="text-xs text-slate-300 leading-relaxed">
                    Glide cursor across computer screens with zero border lag. Local LAN is 100% free forever; remote internet PRO available.
                  </p>

                  {/* Highlights Bullet List */}
                  <div className="space-y-2 text-xs text-slate-400 bg-black/40 p-3.5 rounded-2xl border border-white/5">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-cyan-400 flex-shrink-0" />
                      <span>Infinite Delta Trapping eliminates screen edge cursor sticking</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-emerald-400 flex-shrink-0" />
                      <span>Direct Unicast UDP socket for maximum 144Hz responsiveness</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-purple-400 flex-shrink-0" />
                      <span>Double-tap <code className="text-white font-mono">Ctrl</code> to lock/unlock mouse anytime</span>
                    </div>
                  </div>

                </div>

                {/* Flow Action Bar */}
                <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-2.5">
                  <div className="flex items-center gap-2">
                    <a
                      href="/downloads/ck-flow-windows.zip"
                      download="ck-flow-windows.zip"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-xl text-xs text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors flex items-center gap-1 cursor-pointer"
                      title="Download Portable ZIP"
                    >
                      <FolderDown size={13} />
                      <span>Portable ZIP</span>
                    </a>
                  </div>

                  <a
                    href="/downloads/CKFlow.exe"
                    download="CKFlow.exe"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-2 text-xs font-bold text-black bg-gradient-to-r from-cyan-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.3)] hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] transition-all hover:scale-105 cursor-pointer"
                  >
                    <Download size={14} />
                    <span>Download CK Flow (.exe)</span>
                  </a>
                </div>

              </div>
            )}

            {/* 4. WEB & PWA CARD */}
            {(activeTab === 'all' || activeTab === 'web') && (
              <div className="p-6 sm:p-7 rounded-3xl bg-gradient-to-b from-white/[0.04] via-black/60 to-black/90 border border-white/10 hover:border-cyan-500/50 shadow-2xl transition-all flex flex-col justify-between group relative overflow-hidden lg:col-span-2">
                
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3.5">
                    <div className="w-13 h-13 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 shadow-[0_0_20px_rgba(0,240,255,0.2)] group-hover:scale-105 transition-transform">
                      <Globe size={26} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-white font-display">Web App &amp; Progressive Web App (PWA)</h3>
                        <span className="px-2 py-0.5 text-[9px] font-bold font-mono text-cyan-300 bg-cyan-500/20 border border-cyan-500/40 rounded-full">
                          Zero-Install
                        </span>
                      </div>
                      <p className="text-xs text-slate-400">Chrome, Edge, Safari, Firefox on macOS, Linux, Windows, iOS</p>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      onClose();
                    }}
                    className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold text-black bg-gradient-to-r from-cyan-400 to-purple-400 hover:from-cyan-300 hover:to-purple-300 rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.3)] transition-all hover:scale-105 cursor-pointer"
                  >
                    <Sparkles size={14} />
                    <span>Launch Web App</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mt-5 pt-4 border-t border-white/5 text-xs text-slate-400">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={13} className="text-cyan-400" />
                    <span>Instant room creation &amp; QR pairing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={13} className="text-cyan-400" />
                    <span>Hardware WebCrypto AES-GCM-256</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={13} className="text-cyan-400" />
                    <span>Installable to Desktop / Home Screen</span>
                  </div>
                </div>

              </div>
            )}

          </div>

          {/* Verification & Cryptography Strip */}
          <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-cyan-950/20 via-purple-950/20 to-black/40 border border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3.5 text-xs">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                <ShieldCheck size={18} />
              </div>
              <div>
                <h4 className="font-bold text-white">Cryptographically Verified Ephemeral Relay</h4>
                <p className="text-[11px] text-slate-400">Client-side PBKDF2 + AES-GCM-256 • Zero Server Storage • Open Source</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="https://github.com/chintankas1/ck-share"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white font-semibold inline-flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Github size={14} />
                <span>GitHub Source</span>
              </a>
              <button
                onClick={() => {
                  onClose();
                  onOpenGuide('security');
                }}
                className="text-cyan-400 hover:text-cyan-300 font-semibold inline-flex items-center gap-1 transition-colors cursor-pointer"
              >
                <span>Security Whitepaper</span>
                <ArrowRight size={12} />
              </button>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};
